import React, { useMemo, useState } from 'react';
import { createCn } from '../../../helpers/utils';
import classes from '../styles.module.scss';
import { parseProtocolMarkdown } from '../../../utils/parse-protocol';
import { stripAiPreamble } from '../../../utils/sanitize-content';
import {
  resolveSpeakerRole,
  sortParticipantsByRole,
  formatParticipantMinutes,
  isJobTitleRole,
} from '../../../utils/speaker-roles';
import SpeakerRoleBadge from './speaker-role-badge.component';
import { highlightTechTerms, priorityBadge } from '../../../utils/tech-terms';
import { formatProtocolRichText } from '../../../utils/format-protocol-text';
import { formatTaskDeadline } from '../../../utils/task-deadline';
import { canonicalizeSpeakerDisplayName } from '../../../utils/speaker-display-names';
import { PROTOCOL_AI_DISCLAIMER, PROTOCOL_AI_FOOTER, sanitizeExportModelBranding } from '../../../utils/protocol-footer';
import { buildKtzhProtocolDocument } from '../../../utils/ktzh-protocol-format';
import KtzhProtocolView from './ktzh-protocol-view.component';

const cn = createCn('file', classes);

const SPEAKER_COLORS = ['#1B80FC', '#00B894', '#FF428A', '#F59E0B', '#6C5CE7', '#E17055'];

type Props = {
  protocol: string;
  meetingName: string;
  duration?: string;
  date?: string;
  participantsCount?: number;
  speakerColors?: Record<string, string>;
  editing?: boolean;
  editValue?: string;
  onEditChange?: (value: string) => void;
};

type ProtocolTab = 'ktzh' | 'meeting' | 'decisions' | 'signatures';

function speakerColor(
  name: string,
  speakerKey: string,
  map: Record<string, string>,
  index: number,
): string {
  if (name && map[name]) return map[name];
  if (speakerKey && map[speakerKey]) return map[speakerKey];
  return SPEAKER_COLORS[index % SPEAKER_COLORS.length];
}

const ProtocolView = ({
  protocol,
  meetingName,
  duration,
  date,
  participantsCount,
  speakerColors = {},
  editing = false,
  editValue = '',
  onEditChange,
}: Props) => {
  const [tab, setTab] = useState<ProtocolTab>('meeting');

  const parsed = useMemo(
    () => parseProtocolMarkdown(sanitizeExportModelBranding(stripAiPreamble(protocol))),
    [protocol],
  );

  const ktzhDoc = useMemo(
    () => buildKtzhProtocolDocument(
      sanitizeExportModelBranding(stripAiPreamble(protocol)),
      meetingName,
      date || parsed.meta['Дата'],
    ),
    [protocol, meetingName, date, parsed.meta],
  );

  const sortedParticipants = useMemo(
    () => sortParticipantsByRole(parsed.participants),
    [parsed.participants],
  );

  if (!protocol?.trim() && !editing) {
    return <p className={cn('empty')}>Протокол ещё не сгенерирован.</p>;
  }

  if (editing) {
    return (
      <div className={cn('section-edit-area')}>
        <p className={cn('section-edit-hint')}>
          Редактирование текста протокола (Markdown). После сохранения обновится отображение разделов.
        </p>
        <textarea
          className={cn('section-edit-textarea', { protocol: true })}
          value={editValue}
          onChange={(e) => onEditChange?.(e.target.value)}
          placeholder="Текст протокола…"
          spellCheck
        />
      </div>
    );
  }

  if (!protocol?.trim()) {
    return <p className={cn('empty')}>Протокол ещё не сгенерирован.</p>;
  }

  const displayDate = date || parsed.meta['Дата'] || '';
  const displayDuration = duration || parsed.meta['Длительность'] || '';
  const pCount = participantsCount || parsed.participants.length || 0;

  return (
    <article className={cn('protocol-panel')}>
      <header className={cn('protocol-hero')}>
        <div className={cn('protocol-hero-badge', { official: true })}>Официальный протокол</div>
        <h1 className={cn('protocol-hero-title')}>{meetingName}</h1>
        <div className={cn('protocol-hero-meta', { labeled: true })}>
          {displayDate && <span>Дата: {displayDate}</span>}
          {displayDuration && <span>Длительность: {displayDuration}</span>}
          {pCount > 0 && <span>Участников: {pCount}</span>}
        </div>
      </header>

      <div className={cn('protocol-subtabs')}>
        {([
          ['meeting', 'Ход совещания'],
          ['decisions', 'Решения'],
          ['signatures', 'Подписи'],
          ['ktzh', 'Формат КТЖ'],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={cn('protocol-subtab', { active: tab === id })}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'meeting' && (
        <div className={cn('protocol-tab-body')}>
          {parsed.agenda.length > 0 && (
            <section className={cn('protocol-section')}>
              <h2 className={cn('protocol-section-title')}>Повестка дня</h2>
              <ol className={cn('protocol-agenda')}>
                {parsed.agenda.map((item, i) => (
                  <li
                    key={i}
                    dangerouslySetInnerHTML={{ __html: formatProtocolRichText(item) }}
                  />
                ))}
              </ol>
            </section>
          )}

          {sortedParticipants.length > 0 && (
            <section className={cn('protocol-section')}>
              <h2 className={cn('protocol-section-title')}>
                Состав участников
                <span className={cn('protocol-section-count')}> ({sortedParticipants.length})</span>
              </h2>
              <table className={cn('protocol-table')}>
                <thead>
                  <tr>
                    <th>Участник</th>
                    <th>Роль</th>
                    <th>Время речи</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedParticipants.map((p, i) => {
                    const resolved = resolveSpeakerRole(p.name, p.badgeRole || p.role);
                    const position = isJobTitleRole(p.role) ? p.role : '';
                    return (
                    <tr key={i}>
                      <td>
                        <span
                          className={cn('protocol-speaker-dot')}
                          style={{ background: speakerColor(p.name, p.speakerKey, speakerColors, i) }}
                        />
                        <span className={cn('protocol-speaker-person')}>{resolved.personName}</span>
                        {(position || resolved.orgHint) && (
                          <span className={cn('protocol-speaker-org')}>
                            {position || resolved.orgHint}
                          </span>
                        )}
                        {p.speakerKey && (
                          <span className={cn('protocol-speaker-key')}> {p.speakerKey}</span>
                        )}
                      </td>
                      <td>
                        <SpeakerRoleBadge raw={p.name} explicitRole={p.badgeRole} />
                      </td>
                      <td>{formatParticipantMinutes(p.minutes)}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
          )}

          {parsed.speeches.length > 0 && (
            <section className={cn('protocol-section')}>
              <h2 className={cn('protocol-section-title')}>
                Ход совещания
                <span className={cn('protocol-section-count')}> ({parsed.speeches.length})</span>
              </h2>
              <div className={cn('protocol-speeches')}>
                {parsed.speeches.map((sp, i) => {
                  const resolved = resolveSpeakerRole(sp.title, sp.role);
                  const displayName = canonicalizeSpeakerDisplayName(sp.title.trim())
                    || resolved.personName;
                  const position = sp.position || (isJobTitleRole(sp.role) ? sp.role : '');
                  return (
                    <div key={i} className={cn('protocol-speech-card')}>
                      <div className={cn('protocol-speech-card-header')}>
                        <div>
                          <div className={cn('protocol-speech-name')}>{displayName}</div>
                          {position && (
                            <div className={cn('protocol-speech-position')}>{position}</div>
                          )}
                          {!position && !sp.position && (
                            <div className={cn('protocol-speech-meta')}>
                              <SpeakerRoleBadge raw={sp.title} />
                              {(isJobTitleRole(sp.role) || resolved.orgHint) && (
                                <span className={cn('protocol-speaker-org')}>
                                  {isJobTitleRole(sp.role) ? sp.role : resolved.orgHint}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div
                        className={cn('protocol-speech-text')}
                        dangerouslySetInnerHTML={{ __html: formatProtocolRichText(sp.body) }}
                      />
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      )}

      {tab === 'decisions' && (
        <div className={cn('protocol-tab-body')}>
          {parsed.tasks.length > 0 ? (
            <section className={cn('protocol-section')}>
              <h2 className={cn('protocol-section-title')}>
                {parsed.officialOrders ? 'Решения и поручения' : 'Задачи и поручения'}
              </h2>
              <table className={cn('protocol-table', { tasks: true, orders: parsed.officialOrders })}>
                <thead>
                  <tr>
                    <th>№</th>
                    <th>{parsed.officialOrders ? 'Поручение' : 'Задача'}</th>
                    <th>Ответственный</th>
                    <th>{parsed.officialOrders ? 'Срок исполнения' : 'Срок'}</th>
                    {!parsed.officialOrders && <th>Приоритет</th>}
                  </tr>
                </thead>
                <tbody>
                  {parsed.tasks.map(t => {
                    const badge = priorityBadge(t.priority);
                    return (
                      <tr key={t.num}>
                        <td>{t.num}</td>
                        <td
                          className={cn('protocol-task-cell')}
                          dangerouslySetInnerHTML={{ __html: formatProtocolRichText(t.task) }}
                        />
                        <td>{t.assignee}</td>
                        <td>{formatTaskDeadline(t.deadline)}</td>
                        {!parsed.officialOrders && (
                          <td>
                            <span className={cn('protocol-priority', { [badge.className]: true })}>
                              {badge.label}
                            </span>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
          ) : parsed.decisions.length === 0 ? (
            <p className={cn('empty')}>Раздел поручений не найден в протоколе.</p>
          ) : null}

          {!parsed.officialOrders && parsed.decisions.length > 0 && (
            <section className={cn('protocol-section')}>
              <h2 className={cn('protocol-section-title')}>Принятые решения</h2>
              <table className={cn('protocol-table', { decisions: true })}>
                <thead>
                  <tr>
                    <th>№</th>
                    <th>Решение</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.decisions.map(d => (
                    <tr key={d.num}>
                      <td>{d.num}</td>
                      <td
                        className={cn('protocol-task-cell')}
                        dangerouslySetInnerHTML={{ __html: formatProtocolRichText(d.decision) }}
                      />
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {!parsed.officialOrders && parsed.observations.length > 0 && (
            <section className={cn('protocol-section')}>
              <h2 className={cn('protocol-section-title')}>Наблюдения и риски</h2>
              <table className={cn('protocol-table', { observations: true })}>
                <thead>
                  <tr>
                    <th>№</th>
                    <th>Наблюдение</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.observations.map(o => (
                    <tr key={o.num}>
                      <td>{o.num}</td>
                      <td
                        className={cn('protocol-task-cell')}
                        dangerouslySetInnerHTML={{ __html: formatProtocolRichText(o.observation) }}
                      />
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {!parsed.officialOrders && (parsed.conclusionGroups.length > 0 || parsed.conclusions) && (
            <section className={cn('protocol-section')}>
              <h2 className={cn('protocol-section-title')}>Ключевые выводы</h2>
              {parsed.conclusionGroups.length > 0 ? (
                <div className={cn('protocol-conclusions')}>
                  {parsed.conclusionGroups.map(group => (
                    <div
                      key={group.id + group.title}
                      className={cn('protocol-conclusion-card', { [group.id]: true })}
                    >
                      <h3 className={cn('protocol-conclusion-card-title')}>{group.title}</h3>
                      <ul className={cn('protocol-conclusion-list')}>
                        {group.items.map((item, i) => (
                          <li
                            key={i}
                            dangerouslySetInnerHTML={{ __html: formatProtocolRichText(item) }}
                          />
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  className={cn('protocol-prose')}
                  dangerouslySetInnerHTML={{ __html: formatProtocolRichText(parsed.conclusions) }}
                />
              )}
            </section>
          )}

          {!parsed.officialOrders && parsed.nextSteps.length > 0 && (
            <section className={cn('protocol-section')}>
              <h2 className={cn('protocol-section-title')}>Следующие шаги</h2>
              <ul className={cn('protocol-steps')}>
                {parsed.nextSteps.map((step, i) => (
                  <li key={i} className={cn('protocol-step-item')}>
                    {step.assignee ? (
                      <>
                        <span className={cn('protocol-step-assignee')}>{step.assignee}</span>
                        <span
                          className={cn('protocol-step-action')}
                          dangerouslySetInnerHTML={{ __html: formatProtocolRichText(step.action) }}
                        />
                      </>
                    ) : (
                      <span
                        dangerouslySetInnerHTML={{ __html: formatProtocolRichText(step.action) }}
                      />
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

      {tab === 'signatures' && (
        <div className={cn('protocol-tab-body')}>
          <section className={cn('protocol-section')}>
            <h2 className={cn('protocol-section-title')}>Подписи</h2>
            <div className={cn('protocol-signatures-grid')}>
              <div className={cn('protocol-signature-card')}>
                <span className={cn('signature-dash')} />
                <div className={cn('protocol-signature-label')}>Председатель</div>
                <div className={cn('protocol-signature-name')}>
                  {parsed.signatures.chair || '________________'}
                </div>
              </div>
              <div className={cn('protocol-signature-card')}>
                <span className={cn('signature-dash')} />
                <div className={cn('protocol-signature-label')}>Секретарь</div>
                <div className={cn('protocol-signature-name')}>
                  {parsed.signatures.secretary || '________________'}
                </div>
              </div>
              <div className={cn('protocol-signature-card')}>
                <span className={cn('signature-dash')} />
                <div className={cn('protocol-signature-label')}>Дата</div>
                <div className={cn('protocol-signature-name')}>
                  {parsed.signatures.date || displayDate || '___ __________ 2026 г.'}
                </div>
              </div>
            </div>
          </section>
        </div>
      )}

      {tab === 'ktzh' && (
        <KtzhProtocolView
          doc={ktzhDoc}
          onOpenSpeeches={() => setTab('meeting')}
        />
      )}

      <footer className={cn('protocol-ai-footer')}>
        <div className={cn('protocol-ai-badge')}>
          <span className={cn('protocol-ai-badge-model')}>{PROTOCOL_AI_FOOTER}</span>
          <span className={cn('protocol-ai-badge-disclaimer')}>{PROTOCOL_AI_DISCLAIMER}</span>
        </div>
      </footer>
    </article>
  );
};

export default ProtocolView;
